/**
 * Parse, dedupe, and aggregate story_cost_summary agent events (RAISE-16867 D4/D5/D6/D7).
 *
 * All functions are pure — no side effects, no React.
 * Defensive parsing: cost is the only required field; malformed events return null.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw item shape from GET /api/v1/agent/events */
export interface AgentEventItem {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  work_item_ref: string | null;
  created_at: string;
}

/** A successfully parsed story_cost_summary event. */
export interface StoryCostEvent {
  ref: string; // work_item_ref ?? payload.jira_key — required
  costUsd: number; // payload.cost_usd — required, finite
  sessionId: string | null; // payload.session_id
  storyId: string | null; // payload.story_id
  date: string | null; // payload.date (YYYY-MM-DD)
  createdAt: string; // item.created_at
  repoSlug: string | null; // payload.repo_slug
  epic: string | null; // payload.epic — forward contract, always null today
  developer: string | null; // payload.developer — forward contract, always null today
  byModel: Array<{ model: string; cost_usd: number }>; // optional, [] default
}

export interface StoryAggregate {
  ref: string;
  repoSlug: string | null;
  epic: string | null;
  sessions: number;
  costUsd: number;
  lastDate: string | null;
}

export interface EpicAggregate {
  epic: string;
  stories: number;
  costUsd: number;
}

export interface ActorAggregate {
  developer: string;
  stories: number;
  costUsd: number;
  avgPerStory: number;
}

// ---------------------------------------------------------------------------
// parseStoryCostEvent
// ---------------------------------------------------------------------------

function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

/**
 * Parse a raw AgentEventItem into a StoryCostEvent.
 *
 * Returns null when the payload is malformed (missing or non-numeric cost_usd,
 * missing ref). Malformed rows are never thrown — callers count and skip them.
 */
export function parseStoryCostEvent(item: AgentEventItem): StoryCostEvent | null {
  const p = item.payload;

  // cost_usd is required and must be a finite number
  const rawCost = p["cost_usd"];
  const costUsd = typeof rawCost === "number" && isFinite(rawCost) ? rawCost : null;
  if (costUsd === null) return null;

  // ref: work_item_ref takes priority; fall back to payload.jira_key
  const ref = item.work_item_ref ?? asString(p["jira_key"]);
  if (ref === null) return null;

  // Optional fields — safe defaults
  const sessionId = asString(p["session_id"]);
  const storyId = asString(p["story_id"]);
  const date = asString(p["date"]);
  const repoSlug = asString(p["repo_slug"]);
  const epic = asString(p["epic"]);
  // developer: payload.developer ?? payload.actor — both absent today (D7)
  const developer = asString(p["developer"]) ?? asString(p["actor"]);

  // byModel: optional array of {model, cost_usd}
  const rawByModel = p["cost_breakdown"];
  let byModel: Array<{ model: string; cost_usd: number }> = [];
  if (
    rawByModel !== null &&
    typeof rawByModel === "object" &&
    !Array.isArray(rawByModel)
  ) {
    const breakdown = rawByModel as Record<string, unknown>;
    const rawArr = breakdown["by_model"];
    if (Array.isArray(rawArr)) {
      byModel = rawArr.filter(
        (row): row is { model: string; cost_usd: number } =>
          typeof row === "object" &&
          row !== null &&
          typeof (row as Record<string, unknown>)["model"] === "string" &&
          typeof (row as Record<string, unknown>)["cost_usd"] === "number",
      );
    }
  }

  return {
    ref,
    costUsd,
    sessionId,
    storyId,
    date,
    createdAt: item.created_at,
    repoSlug,
    epic,
    developer,
    byModel,
  };
}

// ---------------------------------------------------------------------------
// dedupeEvents
// ---------------------------------------------------------------------------

/**
 * Dedupe parsed events by (ref, sessionId) key — keep newest createdAt per key.
 *
 * Addresses the RAISE-16865 dup scenario: session-close and story-close can both
 * emit a story_cost_summary for the same (ref, session). The newer event wins.
 */
export function dedupeEvents(events: StoryCostEvent[]): StoryCostEvent[] {
  const map = new Map<string, StoryCostEvent>();
  for (const e of events) {
    const key = `${e.ref}:${e.sessionId ?? "?"}`;
    const existing = map.get(key);
    if (!existing || e.createdAt > existing.createdAt) {
      map.set(key, e);
    }
  }
  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// aggregateByStory
// ---------------------------------------------------------------------------

/**
 * After dedupe: per ref → { ref, repoSlug, epic, sessions, costUsd, lastDate }.
 */
export function aggregateByStory(events: StoryCostEvent[]): StoryAggregate[] {
  const map = new Map<string, StoryAggregate>();
  for (const e of events) {
    const existing = map.get(e.ref);
    if (!existing) {
      map.set(e.ref, {
        ref: e.ref,
        repoSlug: e.repoSlug,
        epic: e.epic,
        sessions: 1,
        costUsd: e.costUsd,
        lastDate: e.date,
      });
    } else {
      existing.sessions += 1;
      existing.costUsd += e.costUsd;
      // Keep the latest date
      if (e.date && (!existing.lastDate || e.date > existing.lastDate)) {
        existing.lastDate = e.date;
      }
    }
  }
  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// aggregateByEpic
// ---------------------------------------------------------------------------

/**
 * Group by epic (null → "unassigned").
 * The epic grouping UI renders only when ≥1 event has a non-null epic (D6).
 * `stories` counts distinct work_item_refs, not events, so multi-session
 * stories are not double-counted.
 */
export function aggregateByEpic(events: StoryCostEvent[]): EpicAggregate[] {
  const map = new Map<string, { refs: Set<string>; costUsd: number }>();
  for (const e of events) {
    const epicKey = e.epic ?? "unassigned";
    const existing = map.get(epicKey);
    if (!existing) {
      map.set(epicKey, { refs: new Set([e.ref]), costUsd: e.costUsd });
    } else {
      existing.refs.add(e.ref);
      existing.costUsd += e.costUsd;
    }
  }
  return Array.from(map.entries()).map(([epic, { refs, costUsd }]) => ({
    epic,
    stories: refs.size,
    costUsd,
  }));
}

// ---------------------------------------------------------------------------
// aggregateByActor
// ---------------------------------------------------------------------------

/**
 * Group by developer (null → "Unattributed").
 * With no attribution, every event lands in one bucket (D7 degraded state).
 * `stories` counts distinct work_item_refs so multi-session stories are not
 * double-counted in avgPerStory.
 */
export function aggregateByActor(events: StoryCostEvent[]): ActorAggregate[] {
  const map = new Map<string, { refs: Set<string>; costUsd: number }>();
  for (const e of events) {
    const devKey = e.developer ?? "Unattributed";
    const existing = map.get(devKey);
    if (!existing) {
      map.set(devKey, { refs: new Set([e.ref]), costUsd: e.costUsd });
    } else {
      existing.refs.add(e.ref);
      existing.costUsd += e.costUsd;
    }
  }
  return Array.from(map.entries()).map(([developer, { refs, costUsd }]) => {
    const stories = refs.size;
    return { developer, stories, costUsd, avgPerStory: costUsd / stories };
  });
}
